import React, { useEffect, useState } from 'react';
import { GlobalStyle } from './components/GlobalStyle';
import { Routes, Route, Navigate, BrowserRouter, useLocation } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { Terminal } from './components/Pages/Terminal';
import { MobileView } from './components/Pages/MobileView';
import { AuthCallback } from './components/Pages/AuthCallback';
import { BillingSuccess } from './components/Pages/BillingSuccess';
import { BillingCancel } from './components/Pages/BillingCancel';
import { AccessBackdrop, AccessBanner, AccessOverlay } from './components/Access';
import routes from './constants/routes';
import { useStore } from './store/useStore';
import { Layout } from './components/Layout';
import { initializeAnalytics, routeToEvent, trackEvent, trackEventOnce } from './services/analytics';
// updated to canonical api

const SITE_URL = 'https://semn.ai';
const OG_IMAGE_URL = `${SITE_URL}/og-image.webp`;
const DEFAULT_TITLE = 'semn.ai | see and hear Bitcoin';
const DEFAULT_DESCRIPTION =
  'See and hear Bitcoin tick by tick. Structural awareness for traders who make irreversible decisions in real time: per-tick microstructure, AI regime context, and sonic encoding.';
const DEFAULT_OG_DESCRIPTION = 'Structural awareness for Bitcoin. See regime, read structure, decide in real time.';

const NOINDEX_PATHS = new Set([routes.AUTH_CALLBACK, routes.BILLING_SUCCESS, routes.BILLING_CANCEL]);

const PAGE_META: Record<string, { title: string; description: string; ogTitle?: string; ogDescription?: string }> = {
  [routes.LANDING]: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogTitle: DEFAULT_TITLE,
    ogDescription: DEFAULT_OG_DESCRIPTION,
  },
  [routes.AUTH_CALLBACK]: {
    title: 'Sign In Callback | semn.ai',
    description: 'Secure sign-in callback flow for semn.ai.',
  },
  [routes.BILLING_SUCCESS]: {
    title: 'Billing Success | semn.ai',
    description: 'Subscription confirmation in progress for semn.ai.',
  },
  [routes.BILLING_CANCEL]: {
    title: 'Billing Canceled | semn.ai',
    description: 'Checkout was canceled. Redirecting back to semn.ai terminal.',
  },
};

const upsertMetaByName = (name: string, content: string) => {
  let meta = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const upsertMetaByProperty = (property: string, content: string) => {
  let meta = document.head.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const upsertCanonical = (href: string) => {
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', href);
};

const RouteMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isKnownPath = Boolean(PAGE_META[pathname]);
    const routeMeta = PAGE_META[pathname] ?? PAGE_META[routes.LANDING];
    const canonicalPath = isKnownPath ? (pathname === routes.LANDING ? '/' : pathname) : '/';
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const robotsContent = NOINDEX_PATHS.has(pathname) ? 'noindex, nofollow' : 'index, follow';

    document.title = routeMeta.title;
    upsertMetaByName('description', routeMeta.description);
    upsertMetaByName('robots', robotsContent);
    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:title', routeMeta.title);
    upsertMetaByName('twitter:description', routeMeta.description);
    upsertMetaByName('twitter:image', OG_IMAGE_URL);
    upsertMetaByName('twitter:image:alt', 'semn.ai Bitcoin structural awareness terminal');
    upsertMetaByProperty('og:type', 'website');
    upsertMetaByProperty('og:site_name', 'semn.ai');
    upsertMetaByProperty('og:title', routeMeta.ogTitle ?? routeMeta.title);
    upsertMetaByProperty('og:description', routeMeta.ogDescription ?? routeMeta.description);
    upsertMetaByProperty('og:url', canonicalUrl);
    upsertMetaByProperty('og:image', OG_IMAGE_URL);
    upsertMetaByProperty('og:image:secure_url', OG_IMAGE_URL);
    upsertMetaByProperty('og:image:type', 'image/webp');
    upsertMetaByProperty('og:image:alt', 'semn.ai Bitcoin structural awareness terminal');
    upsertCanonical(canonicalUrl);
  }, [pathname]);

  return null;
};

const RouteAnalytics = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    trackEvent(routeToEvent(pathname));
  }, [pathname]);

  return null;
};

const App = () => {
  const { initialize, beginPreview, appState, blockedReason, canStartPreview, hasInitialSnapshot } =
    useStore();
  const [showOverlay, setShowOverlay] = useState(false);
  const [blockedDismissed, setBlockedDismissed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 920px)').matches;
  });

  const entitlementReady = appState === 'preview' || appState === 'trial' || appState === 'active';
  const showBlockedBackdrop = appState === 'blocked';
  const showAccessBackdrop =
    appState === 'loading' || showBlockedBackdrop || (entitlementReady && !hasInitialSnapshot);

  useEffect(() => {
    initializeAnalytics();
    trackEvent('app_opened');
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (canStartPreview) {
      beginPreview();
    }
  }, [canStartPreview, beginPreview]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 920px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileView(event.matches);
    };

    setIsMobileView(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    trackEventOnce(isMobileView ? 'device_mobile' : 'device_desktop');
  }, [isMobileView]);

  const handleUpgradeClick = () => {
    if (appState === 'trial') {
      trackEvent('upgrade_overlay_open_trial');
    } else if (appState === 'preview') {
      trackEvent('upgrade_overlay_open_preview');
    } else if (appState === 'blocked') {
      trackEvent('upgrade_overlay_open_blocked');
    } else {
      trackEvent('upgrade_overlay_open_other');
    }
    setShowOverlay(true);
  };

  const handleCloseOverlay = () => {
    trackEvent('upgrade_overlay_close');
    setShowOverlay(false);
  };

  const handleDismissBlockedOverlay = () => {
    trackEvent('blocked_overlay_dismiss');
    setBlockedDismissed(true);
  };

  return (
    <>
      <Layout>
        <GlobalStyle />
        <BrowserRouter>
          <RouteAnalytics />
          <RouteMetadata />
          <Routes>
            <Route
              path={routes.LANDING}
              element={
                <>
                  {!isMobileView && <AccessBanner onUpgradeClick={handleUpgradeClick} />}
                  {isMobileView ? <MobileView onUpgradeClick={handleUpgradeClick} /> : <Terminal />}
                  {!isMobileView && <AccessBackdrop active={showAccessBackdrop} />}
                  {appState === 'blocked' && blockedReason !== 'new' && !blockedDismissed && (
                    <AccessOverlay onClose={handleDismissBlockedOverlay} />
                  )}
                  {showOverlay && (appState !== 'blocked' || blockedDismissed) && (
                    <AccessOverlay
                      initialMode={appState === 'trial' ? 'subscribe' : 'email'}
                      onClose={handleCloseOverlay}
                    />
                  )}
                </>
              }
            />
            <Route path={routes.TERMINAL} element={<Navigate to={routes.LANDING} replace />} />
            <Route path={routes.AUTH_CALLBACK} element={<AuthCallback />} />
            <Route path={routes.BILLING_SUCCESS} element={<BillingSuccess />} />
            <Route path={routes.BILLING_CANCEL} element={<BillingCancel />} />
            <Route path="*" element={<Navigate to={routes.LANDING} replace />} />
          </Routes>
        </BrowserRouter>
      </Layout>
    </>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<App />);

if (import.meta.hot) {
  import.meta.hot.accept();
}
